// client/src/components/comunicacao/manual/ComunicacaoManualPage.js
import React, { useEffect, useCallback, useState } from 'react';
import { useComunicacaoConfigs, useMutateComunicacaoConfig } from '../../../hooks/useComunicacaoConfig';
import useAlert from '../../../hooks/useAlert';
import { Loader2, MessageCircle, Info, Trash2 } from 'lucide-react';
import ComunicacaoManualRow from './ComunicacaoManualRow';
import PageHeader from '../../layout/PageHeader';
import ActionsDrawer from '../../common/ActionsDrawer';

// ✅ Envio manual não depende mais de uma configuração pré-criada — o
// template é escolhido diretamente na hora do envio, a partir da tela do
// próprio agendamento (AgendamentoActionsModal.js). Esta tela deixa de
// permitir criar novas configurações (o backend não aceita mais tipo:'Manual'
// sem os campos de escopo antigos) — mantém só a listagem/exclusão de
// registros legados, sem apagar dados existentes.
const ComunicacaoManualPage = () => {
  const { setAlert } = useAlert();

  const { data: configsData, isLoading, isError } = useComunicacaoConfigs({ tipo: 'Manual' });
  const { deleteConfig } = useMutateComunicacaoConfig();

  const configs = configsData || [];
  const [selectedConfig, setSelectedConfig] = useState(null);

  useEffect(() => {
    if (isError) setAlert('Erro ao carregar configurações de comunicação', 'danger');
  }, [isError, setAlert]);

  const handleDelete = useCallback((id) => {
    if (window.confirm('Deseja realmente excluir esta configuração de comunicação manual?')) {
      deleteConfig.mutate(id, { onSuccess: () => setSelectedConfig(null) });
    }
  }, [deleteConfig]);

  return (
    <>
      <div className="page-management-container">
        <PageHeader
          icon={<MessageCircle />}
          title="Comunicação Manual"
          subtitle={!isLoading && configs.length > 0 ? `${configs.length} configuração${configs.length !== 1 ? 'ões' : ''} (legado)` : null}
        />

        <div className="content-panel" style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '1rem 1.25rem' }}>
          <Info size={20} style={{ flexShrink: 0, color: 'var(--color-primary)' }} />
          <p style={{ margin: 0, fontSize: '0.9rem' }}>
            O envio manual de mensagens agora escolhe o template diretamente na hora do envio,
            a partir da tela do próprio agendamento — não é mais necessário criar uma configuração aqui.
            Os registros abaixo são legados e só podem ser removidos, não editados.
          </p>
        </div>
      </div>

      <div className="table-container">
        <div className="table-wrapper">
          {isLoading ? (
            <div className="table-loading-state">
              <Loader2 size={24} className="animate-spin"/>
              <span>Carregando configurações...</span>
            </div>
          ) : configs.length > 0 ? (
            <table className="styled-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Nome</th>
                  <th>Evento</th>
                  <th>Canal</th>
                  <th>Ativo</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {configs.map(config => (
                  <ComunicacaoManualRow
                    key={config._id}
                    config={config}
                    onOpenActions={setSelectedConfig}
                    isDeleting={deleteConfig.isPending}
                  />
                ))}
              </tbody>
            </table>
          ) : (
            <div className="table-empty-state">
              <MessageCircle size={48}/>
              <h3>Nenhuma configuração manual legada</h3>
            </div>
          )}
        </div>
      </div>

      <ActionsDrawer
        open={!!selectedConfig}
        onClose={() => setSelectedConfig(null)}
        title={selectedConfig?.nome || 'Configuração Manual'}
        actions={selectedConfig ? [
          {
            key: 'excluir',
            label: 'Excluir',
            icon: <Trash2 size={20} className="icon" />,
            onClick: () => handleDelete(selectedConfig._id),
            destructive: true,
            loading: deleteConfig.isPending,
          },
        ] : []}
      />
    </>
  );
};

export default ComunicacaoManualPage;
